import { Trans, useTranslation } from 'react-i18next';

import type { ForumPost } from '~/api/forum/types';

import { useDeleteForumPost } from '~/api/forum/useDeleteForumPost';
import { AlertDialog, Button, ExternalLink } from '~/components';
import { toast } from '~/utils/toast';

type Props = {
  forumPost: ForumPost;
  onClose: () => void;
  onDeleted: () => void;
};

export const ConfirmDeleteForumPostDialog = ({ forumPost, onClose, onDeleted }: Props) => {
  const mutation = useDeleteForumPost(forumPost.id);

  const { t } = useTranslation(['forum']);

  const onDelete = () =>
    mutation.mutate(undefined, {
      onSuccess: () => {
        toast.success({ description: t('forum:alerts.deletePost.success') });
        onDeleted();
      },
      onError: () => toast.danger({ description: t('forum:alerts.deletePost.error') }),
    });

  return (
    <AlertDialog.Body status={forumPost.deletable ? 'danger' : 'info'}>
      <AlertDialog.Title>{t(forumPost.deletable ? 'forum:deletePost.title' : 'forum:deletePost.titleUnable')}</AlertDialog.Title>

      <AlertDialog.Description>
        {forumPost.deletable ? (
          t('forum:deletePost.description', { title: forumPost.title })
        ) : (
          <Trans components={{ emailLink: <ExternalLink /> }} i18nKey="forum:deletePost.descriptionUnable" />
        )}
      </AlertDialog.Description>

      <AlertDialog.Actions>
        {forumPost.deletable ? (
          <Button emphasis="status" icon="Trash" loading={mutation.isPending} onClick={onDelete}>
            {t('forum:deletePost.actions.confirm')}
          </Button>
        ) : (
          <Button onClick={onClose}>{t('forum:deletePost.actions.confirmUnable')}</Button>
        )}
      </AlertDialog.Actions>
    </AlertDialog.Body>
  );
};
